import type React from 'react'
import type { FieldConfig, FormikConfig, FormikValues } from 'formik'

import type { JSX } from '@23/web-components'

import { TtCheckboxFormik } from './TtCheckboxFormik'
import { TtDatePickerFormik } from './TtDatePickerFormik'
import { TtFieldsetFormik } from './TtFieldsetFormik'
import { TtFileInputFormik } from './TtFileInputFormik'
import { TtFormFormik } from './TtFormFormik'
import { TtNumberFieldFormik } from './TtNumberFieldFormik'
import { TtOptionFormik } from './TtOptionFormik'
import { TtRadioFormik } from './TtRadioFormik'
import { TtRangeFormik } from './TtRangeFormik'
import { TtSelectFormik } from './TtSelectFormik'
import { TtSubmitButtonFormik } from './TtSubmitButton'
import { TtTextAreaFormik } from './TtTextAreaFormik'
import { TtTextFieldFormik } from './TtTextFieldFormik'
import { TtToggleFormik } from './TtToggleFormik'
import { TtWysiwygFormik } from './TtWysiwygFormik'

export interface StyleReactProps {
  class?: string
  className?: string
  style?: {
    [key: string]: any
  }
}

type SupportedFormControls = {
  'tt-text-field': {
    component: JSX.TtTextField
    element: HTMLTtTextFieldElement
  }
  'tt-text-area': {
    component: JSX.TtTextArea
    element: HTMLTtTextAreaElement
  }
  'tt-select': {
    component: JSX.TtSelect
    element: HTMLTtSelectElement
  }
  'tt-radio': {
    component: JSX.TtRadio
    element: HTMLTtRadioElement
  }
  'tt-checkbox': {
    component: JSX.TtCheckbox
    element: HTMLTtCheckboxElement
  }
  'tt-toggle': {
    component: JSX.TtToggle
    element: HTMLTtToggleElement
  }
  'tt-file-input': {
    component: JSX.TtFileInput
    element: HTMLTtFileInputElement
  }
  'tt-range': {
    component: JSX.TtRange
    element: HTMLTtRangeElement
  }
  'tt-number-field': {
    component: JSX.TtNumberField
    element: HTMLTtNumberFieldElement
  }
  'tt-date-picker': {
    component: JSX.TtDatePicker
    element: HTMLTtDatePickerElement
  }
  'tt-fieldset': {
    component: JSX.TtFieldset
    element: HTMLTtFieldsetElement
  }
  'tt-wysiwyg': {
    component: JSX.TtWysiwyg
    element: HTMLTtWysiwygElement
  }
  'tt-option': {
    component: JSX.TtOption
    element: HTMLTtOptionElement
  }
}

export type FormikFormControlProps<
  TtFormControl extends keyof SupportedFormControls,
> = SupportedFormControls[TtFormControl]['component'] &
  Omit<
    React.HTMLAttributes<SupportedFormControls[TtFormControl]['element']>,
    keyof StyleReactProps | keyof FieldConfig
  > &
  StyleReactProps &
  FieldConfig

export type FormikFormProps<FormValues extends FormikValues> = Pick<
  JSX.TtForm,
  'label' | 'novalidate' | 'submitOnChange'
> &
  Omit<
    React.HTMLAttributes<HTMLTtFormElement>,
    keyof StyleReactProps | keyof FormikConfig<FormValues>
  > &
  StyleReactProps &
  FormikConfig<FormValues>

export type FieldNameMatchingAcceptedType<
  FormValues extends FormikValues,
  AcceptedTypes,
> = keyof {
  [Name in keyof FormValues as FormValues[Name] extends AcceptedTypes
    ? Name
    : never]: 0
}

export default {
  Form: TtFormFormik,
  SubmitButton: TtSubmitButtonFormik,
  TextField: TtTextFieldFormik,
  TextArea: TtTextAreaFormik,
  Radio: TtRadioFormik,
  Checkbox: TtCheckboxFormik,
  Select: TtSelectFormik,
  FileInput: TtFileInputFormik,
  NumberField: TtNumberFieldFormik,
  Range: TtRangeFormik,
  Toggle: TtToggleFormik,
  DatePicker: TtDatePickerFormik,
  Fieldset: TtFieldsetFormik,
  Wysiwyg: TtWysiwygFormik,
  Option: TtOptionFormik,
}
